/**
 * Pure derivation for one review report's pipeline (YAG-440) — moved verbatim
 * from ReviewReportDialog so the review case's Specialists section and the
 * dialog it replaces can never disagree about what ran, what was carried, and
 * what verification said. Honesty rules, all load-bearing:
 *   - only recorded states print: a stage with no record on a live run is
 *     "reviewing now" or "waiting"; on a terminal run it says "did not run".
 *   - kept/dropped marks are a reconstruction ("carried / not carried"),
 *     never presented as the model's per-finding verdicts.
 */
import type {
  CodeReviewReport,
  CodeReviewReportStage,
  RefinementDecision,
  RefinementDecisions,
  ReviewFinding,
} from "@yagni/shared";
import {
  ROSTER_NO_FINDINGS,
  ROSTER_NOT_REACHED,
  ROSTER_RUNNING,
  ROSTER_WAITING,
  SKIP_REASON_COPY,
  VERIFICATION_FAILED_NOTE,
  carriedCountLine,
  chunkedRetrySuffix,
  findingsCountLabel,
  followUpMark,
  ranViaSuffix,
  refinementDegradedSuffix,
  refinementRosterNote,
  stageFailedNote,
  stageLabel,
  unreadSuffix,
  verificationRosterNote,
} from "../../workers/copy.js";
import type { DiffCheckMarkFlags } from "../../workers/copy.js";

/** prepare, the whole-diff pass, roles, refinement, verification last. */
function stageOrder(key: string): number {
  if (key === "prepare") return 0;
  if (key === "deep" || key === "diff_check") return 1;
  if (key === "refinement") return 3;
  if (key === "verification") return 4;
  return 2;
}

/** A stage output that quacks like a StageResult; anything else renders raw-less. */
export interface StageResultShape {
  findings?: ReviewFinding[];
  failed?: boolean;
  skipped?: string | null;
  verdict?: string | null;
  /**
   * The follow-up check's shape (YAG-357). It reports what happened to prior
   * comments rather than raising new findings, which is why a reader looking
   * only for `findings` printed "no findings" over the whole thing.
   */
  entries?: DiffCheckEntryShape[];
  /** YAG-490 error record: why a failed stage failed. Absent on older reports. */
  errorCode?: string;
  errorDetail?: string;
  /** YAG-490: the stage succeeded via tier failover — a quiet record line. */
  failover?: { fromTier?: string; toTier?: string };
  /** YAG-490: the agentic loop degraded to single-shot (provenance only). */
  loopFallback?: { code?: string; detail?: string };
  /** The pass never read the code; it is neither failed nor a check that ran. */
  unread?: boolean;
  /** YAG-551: the stage succeeded via chunked retry after a context-overflow 400. */
  chunkedRetry?: { chunks?: number; trigger?: string; promptTokens?: number; modelMax?: number };
  /** YAG-551: refinement succeeded on a reduced input (progressive degradation). */
  refinementDegraded?: "capped_candidates" | "diff_windows" | "no_diff";
}

/** One prior comment, re-judged against the new diff. */
export interface DiffCheckEntryShape {
  key?: string;
  classification?: string;
  note?: string | null;
  path?: string | null;
  line?: number | null;
  severity?: string | null;
  role?: string | null;
  /** True when the entry was emitted deterministically by the pinned-prior
   * pre-filter, not judged by the model. Absent on older reports = false. */
  pinned?: boolean;
  /** False when a persists/partially_addressed claim named no live current-code
   * evidence — "never actually examined", distinguishable from a verified
   * claim. Absent on older reports = treat as proven. */
  evidenceReady?: boolean;
}

export function asStageResult(output: unknown): StageResultShape | null {
  if (!output || typeof output !== "object" || Array.isArray(output)) return null;
  return output as StageResultShape;
}

/** The prepare output, as far as this surface trusts it. `roles` is the planned
 * roster (YAG-417); reports from before it was recorded render without one. */
export interface PrepareOutputShape {
  trivial?: boolean;
  /** Reports written before YAG-425 carry a complexity tier instead. */
  complexity?: string;
  fromCache?: boolean;
  followUp?: boolean;
  roles?: { slug?: unknown; name?: unknown; skipped?: unknown }[];
}

export function asPrepareOutput(output: unknown): PrepareOutputShape | null {
  if (!output || typeof output !== "object" || Array.isArray(output)) return null;
  return output as PrepareOutputShape;
}

interface RosterRole {
  slug: string;
  name: string;
  skipped: string | null;
}

function plannedRoles(prepare: PrepareOutputShape | null): RosterRole[] | null {
  if (!prepare || !Array.isArray(prepare.roles)) return null;
  const roles: RosterRole[] = [];
  for (const role of prepare.roles) {
    if (typeof role?.slug !== "string") continue;
    roles.push({
      slug: role.slug,
      name: typeof role.name === "string" ? role.name : role.slug,
      skipped: typeof role.skipped === "string" ? role.skipped : null,
    });
  }
  return roles;
}

export type PipelineState = "done" | "failed" | "skipped" | "running" | "waiting" | "not_reached";

/**
 * The whole pipeline as one ordered list: recorded stages where they exist,
 * planned-but-unwritten ones from the prepare roster. Without a roster (older
 * reports) the list is exactly the recorded stages. Prepare is machinery, not
 * a specialist — its facts ride the summary card, never a row here.
 */
export interface PipelineEntry {
  key: string;
  label: string;
  state: PipelineState;
  stage: CodeReviewReportStage | null;
  result: StageResultShape | null;
  findingsCount: number;
  /**
   * How many of this stage's findings refinement kept — the number the
   * roster counts, so it speaks the summary's language. Null until decisions
   * exist (live runs, older reports): raw counts are the honest fallback there.
   */
  carriedCount: number | null;
  skipReason: string | null;
}

/** One planned row's inputs, as `entryState` needs them. */
interface EntryStateInput {
  key: string;
  stage: CodeReviewReportStage | null;
  result: StageResultShape | null;
  rosterSkip: string | null;
  isLive: boolean;
  /** The roles phase is live: the pass row recorded, roles fan out together. */
  rolesParallel: boolean;
  /** Every role has a record or a roster skip — refinement's frontier rule. */
  refinementFrontierReady: boolean;
  /** Has any earlier row already claimed the live run's "running" slot? */
  runningAssigned: boolean;
}

/**
 * The per-row state decision, extracted so each rule reads on its own:
 *   1. recorded stages — skipped/failed/done, from the record itself;
 *   2. roster-skipped planned roles — skipped with the plan's reason;
 *   3. refinement — the frontier: running only when every role settled;
 *   4. live parallel roles — every unrecorded role runs together;
 *   5. live sequential — the first unrecorded row runs, the rest wait;
 *   6. terminal — unrecorded rows never ran.
 * Returns whether this row consumed the "running" slot, so the caller maps
 * rows in order without a closure carrying mutable state across branches.
 */
function entryState(input: EntryStateInput): {
  state: PipelineState;
  skipReason: string | null;
  consumedRunning: boolean;
} {
  if (input.stage) {
    // A recorded failure is FAILED, not done: rendering a failed check as
    // "done / no findings" read as a clean pass — the exact lie the Aug 11
    // incident surfaced.
    return {
      state: input.result?.skipped
        ? "skipped"
        : input.result?.failed === true
          ? "failed"
          : "done",
      skipReason: input.result?.skipped ?? null,
      consumedRunning: false,
    };
  }
  if (input.rosterSkip) {
    return { state: "skipped", skipReason: input.rosterSkip, consumedRunning: false };
  }
  if (input.key === "refinement") {
    // The frontier: running only when every role has a record or a skip.
    // Verification never reaches here unrecorded — a row for it exists only
    // when the pass recorded, so there is no frontier to guard.
    if (input.isLive && input.refinementFrontierReady) {
      return { state: "running", skipReason: null, consumedRunning: true };
    }
    return {
      state: input.isLive ? "waiting" : "not_reached",
      skipReason: null,
      consumedRunning: false,
    };
  }
  if (input.rolesParallel) {
    // A planned role with no record while the roles phase is live: it IS
    // running — the backend fans the role passes out together.
    return { state: "running", skipReason: null, consumedRunning: true };
  }
  if (input.isLive && !input.runningAssigned) {
    return { state: "running", skipReason: null, consumedRunning: true };
  }
  return {
    state: input.isLive ? "waiting" : "not_reached",
    skipReason: null,
    consumedRunning: false,
  };
}

export function buildPipeline(report: CodeReviewReport): PipelineEntry[] {
  const prepare = asPrepareOutput(report.stages["prepare"]?.output ?? null);
  const roster = plannedRoles(prepare);
  const isLive = report.status === "in_progress";

  const planned: { key: string; label: string; rosterSkip: string | null }[] = [];
  const seen = new Set<string>(["prepare"]);
  const add = (key: string, label: string, rosterSkip: string | null = null) => {
    if (seen.has(key)) return;
    seen.add(key);
    planned.push({ key, label, rosterSkip });
  };

  if (roster) {
    const passKey = prepare?.followUp || report.stages["diff_check"] ? "diff_check" : "deep";
    add(passKey, stageLabel(passKey));
    for (const role of roster) {
      // Lowercased for the surface's voice: role display names arrive
      // capitalized from the Playbook, every fixed label here is lowercase.
      add(`role:${role.slug}`, role.name.toLowerCase(), role.skipped);
    }
    // Anything recorded that the plan did not name still gets a row: the
    // record wins over the plan, always. Refinement and verification are
    // pinned last, in that order — this loop inserts before them.
    for (const key of Object.keys(report.stages).sort(
      (a, b) => stageOrder(a) - stageOrder(b) || a.localeCompare(b),
    )) {
      if (key !== "refinement" && key !== "verification") add(key, stageLabel(key));
    }
    add("refinement", stageLabel("refinement"));
    if (report.stages["verification"]) add("verification", stageLabel("verification"));
  } else {
    for (const key of Object.keys(report.stages).sort(
      (a, b) => stageOrder(a) - stageOrder(b) || a.localeCompare(b),
    )) {
      add(key, stageLabel(key));
    }
  }

  // No row claims "reviewing now" while prepare itself is still running —
  // the card's in-progress chip carries that window.
  let runningAssigned: boolean = isLive && !("prepare" in report.stages);
  // Role passes run in PARALLEL (one activity per role group, review.ts):
  // once the pass row is recorded on a live report, every unrecorded planned
  // role is genuinely running, not waiting its turn. Refinement keeps the
  // frontier: it runs after the roles complete.
  const rolesParallel =
    isLive && ("deep" in report.stages || "diff_check" in report.stages);
  const refinementFrontierReady = planned.every((p) => {
    if (p.key === "refinement" || p.key === "verification") return true;
    return report.stages[p.key] != null || p.rosterSkip != null;
  });

  return planned.map(({ key, label, rosterSkip }) => {
    const stage = report.stages[key] ?? null;
    const result = stage ? asStageResult(stage.output) : null;
    const isRefinement = key === "refinement";
    const findingsCount =
      isRefinement && report.refinementDecisions
        ? report.refinementDecisions.survivors.length
        : (result?.findings?.length ?? 0);
    const carriedCount =
      !isRefinement && report.refinementDecisions && findingsCount > 0
        ? report.refinementDecisions.decisions.filter(
            (d) => d.stage === key && d.decision === "kept",
          ).length
        : null;

    const decision = entryState({
      key,
      stage,
      result,
      rosterSkip,
      isLive,
      rolesParallel,
      refinementFrontierReady,
      runningAssigned,
    });
    if (decision.consumedRunning) runningAssigned = true;
    const { state, skipReason } = decision;
    return { key, label, state, stage, result, findingsCount, carriedCount, skipReason };
  });
}

/** "5 resolved · 1 persists" from the follow-up check's own entries.
 * Pinned entries are EXCLUDED — they are provenance-flagged bookkeeping,
 * counted once by countPinned and appended as "N held resolved" by the
 * outcome line; counting them here too would read "2 addressed · 2 held
 * resolved" for two entries. */
export function countClassifications(
  entries: { classification?: string; pinned?: boolean }[],
): Record<string, number> {
  const counts: Record<string, number> = {};
  for (const entry of entries) {
    if (entry.pinned === true) continue;
    if (!entry.classification) continue;
    counts[entry.classification] = (counts[entry.classification] ?? 0) + 1;
  }
  return counts;
}

/** Count of entries the pinned-prior pre-filter emitted deterministically. */
export function countPinned(entries: DiffCheckEntryShape[]): number {
  return entries.filter((e) => e.pinned === true).length;
}

/** One prior-comment row's mark: the classification word plus the honesty
 * suffixes — a pinned prior names its provenance, an unproven persists
 * names its missing evidence. Older reports carry neither flag and print
 * the plain classification word. */
export function diffCheckMark(
  entry: Pick<DiffCheckEntryShape, "classification" | "pinned" | "evidenceReady">,
  note: string | null,
): string | null {
  if (!entry.classification) return null;
  const flags: DiffCheckMarkFlags = {};
  if (entry.pinned === true) flags.pinned = true;
  if (entry.evidenceReady === false) flags.evidenceReady = false;
  return followUpMark(entry.classification, note, flags);
}

export function rosterNote(entry: PipelineEntry, report: CodeReviewReport): string {
  if (entry.state === "running") return ROSTER_RUNNING;
  if (entry.state === "waiting") return ROSTER_WAITING;
  if (entry.state === "not_reached") return ROSTER_NOT_REACHED;
  if (entry.state === "skipped") {
    return `skipped · ${SKIP_REASON_COPY[entry.skipReason ?? ""] ?? entry.skipReason}`;
  }
  if (entry.key === "verification") {
    // Verification failure has its own copy (findings pass through unchecked)
    // — checked before the generic failed note on purpose.
    if (entry.result?.failed) return VERIFICATION_FAILED_NOTE;
    const output = (entry.stage?.output ?? {}) as {
      confirmed?: number;
      refuted?: number;
      unverified?: number;
    };
    return verificationRosterNote(output);
  }
  // A failed check names its persisted reason (YAG-490) — the 10-second
  // lookup this pane owed the Aug 11 incident.
  if (entry.state === "failed") {
    return stageFailedNote(entry.result?.errorCode, entry.result?.errorDetail);
  }
  // Succeeded rows carry the tier-failover substitution as a quiet suffix.
  const failoverSuffix =
    typeof entry.result?.failover?.toTier === "string"
      ? ranViaSuffix(entry.result.failover.toTier)
      : "";
  // YAG-551: chunked retry / refinement degradation provenance.
  const chunkedSuffix =
    typeof entry.result?.chunkedRetry?.chunks === "number"
      ? chunkedRetrySuffix(entry.result.chunkedRetry.chunks)
      : "";
  const degradedSuffix =
    typeof entry.result?.refinementDegraded === "string"
      ? refinementDegradedSuffix(entry.result.refinementDegraded)
      : "";
  const provenanceSuffix =
    failoverSuffix +
    chunkedSuffix +
    degradedSuffix +
    (entry.result?.unread === true ? unreadSuffix() : "");
  if (entry.key === "refinement" && report.refinementDecisions) {
    return refinementRosterNote(report.refinementDecisions) + provenanceSuffix;
  }
  if (entry.carriedCount !== null) {
    return carriedCountLine(entry.carriedCount, entry.findingsCount) + provenanceSuffix;
  }
  return (
    (entry.findingsCount > 0 ? findingsCountLabel(entry.findingsCount) : ROSTER_NO_FINDINGS) +
    provenanceSuffix
  );
}

export function decisionFor(
  decisions: RefinementDecisions | null,
  stage: string,
  findingId: string,
): RefinementDecision | null {
  if (!decisions) return null;
  return decisions.decisions.find((d) => d.stage === stage && d.id === findingId) ?? null;
}

/** Marker lines are delivery plumbing, never content. */
export function stripMarkerLines(body: string): string {
  return body
    .split("\n")
    .filter((line) => !line.trim().startsWith("<!--"))
    .join("\n")
    .trim();
}
